/**
 * Quality checks for the reason a submitter gives when leaving a soft-required field blank.
 *
 * A soft-required field is a governance gate that the submitter is allowed to walk past —
 * the price of admission is an explanation that goes on the record. A reason of "n/a" or
 * "." satisfies "not blank" while explaining nothing, which makes the audit entry worse than
 * useless: it looks like a considered waiver and is not one. These checks are deliberately
 * mechanical (length, word count, a small list of non-answers) rather than a judgement about
 * whether the explanation is any GOOD — that is a human's call, and no automated check
 * should be able to reject a genuine explanation it happens not to like.
 *
 * Thresholds are firm configuration (`settings/fieldRules.bypassReasons`), edited from
 * Admin → Field rules. Mirrored EXACTLY in functions/src/lib/bypassReason.js — the server
 * re-checks at submit, and the two must agree or the form will let through what the callable
 * rejects. Keep them in sync.
 */

export const DEFAULT_BYPASS_REASON_RULES = { minCharacters: 15, minWords: 3 };

/**
 * Strings that are grammatically a reason and semantically a refusal to give one. Compared
 * after stripping everything but letters, so "N/A", "n.a.", "N / A" all collapse to "na".
 */
const NON_ANSWERS = new Set([
  'na',
  'n',
  'no',
  'none',
  'nil',
  'nothing',
  'notapplicable',
  'notavailable',
  'notneeded',
  'notrequired',
  'unknown',
  'unsure',
  'idk',
  'idontknow',
  'dontknow',
  'tbd',
  'tba',
  'pending',
  'later',
  'blank',
  'empty',
  'test',
  'testing',
  'asdf',
  'asdfasdf',
  'qwerty',
  'x',
  'xx',
  'xxx',
  'seeabove',
  'asdiscussed',
  'same',
  'sameasabove',
  'ditto',
  'refused',
  'declined',
  'skip',
  'skipped',
  'ok',
  'okay',
  'yes',
]);

export function resolveBypassReasonRules(config) {
  // Accepted ranges match adminActions.validateBypassReasons, so a value hand-written into
  // Firestore outside those bounds -- including a 0 that would quietly switch the check off
  // -- falls back to the default instead of weakening the gate.
  const raw = config || {};
  const min = Number(raw.minCharacters);
  const words = Number(raw.minWords);
  return {
    minCharacters:
      Number.isFinite(min) && min >= 1 && min <= 500
        ? Math.round(min)
        : DEFAULT_BYPASS_REASON_RULES.minCharacters,
    minWords:
      Number.isFinite(words) && words >= 1 && words <= 50
        ? Math.round(words)
        : DEFAULT_BYPASS_REASON_RULES.minWords,
  };
}

/** Tokens that are actually words: at least two letters, punctuation and digits ignored. */
function words(reason) {
  return String(reason || '')
    .split(/[^A-Za-z\u00C0-\u024F']+/)
    .filter((token) => token.replace(/'/g, '').length >= 2);
}

const lettersOnly = (reason) => String(reason || '').replace(/[^A-Za-z\u00C0-\u024F]/g, '').toLowerCase();

/**
 * @returns {string|null} why this reason is not acceptable, phrased for the submitter, or
 *   null if it passes. A BLANK reason returns null: "no reason given" is the field still
 *   being unanswered, which the soft-required warning itself already reports — returning a
 *   complaint here too would double-report the same thing.
 */
export function bypassReasonProblem(reason, config) {
  const text = String(reason == null ? '' : reason).trim();
  if (!text) return null;

  const { minCharacters, minWords } = resolveBypassReasonRules(config);
  const collapsed = lettersOnly(text);

  if (!collapsed) return 'Give a reason in words, not just punctuation or numbers.';
  if (NON_ANSWERS.has(collapsed)) {
    return `“${text}” does not explain anything — say why the field is being left blank.`;
  }
  // A single character repeated ("aaaaaaaaaaaaaaaa") passes length and would pass a naive
  // word count if spaced out.
  if (new Set(collapsed).size === 1) return 'Give a reason in words, not repeated characters.';
  if (text.length < minCharacters) {
    return `Add a little more detail — at least ${minCharacters} characters.`;
  }
  const wordCount = words(text).length;
  if (wordCount < minWords) {
    return `Explain in at least ${minWords} words — this has ${wordCount === 1 ? 'only 1' : wordCount}.`;
  }
  return null;
}

/**
 * Whether a reason counts as a recorded waiver, i.e. whether the soft-required warning it
 * answers should stop blocking submission. Blank is not a waiver; neither is a non-answer.
 */
export function isAcceptedBypassReason(reason, config) {
  const text = String(reason == null ? '' : reason).trim();
  if (!text) return false;
  return bypassReasonProblem(text, config) === null;
}
